import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { IModelService } from 'vscode/vscode/vs/editor/common/services/model';
import { IFileService } from 'vscode/vscode/vs/platform/files/common/files.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { ILogService } from 'vscode/vscode/vs/platform/log/common/log.service';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { IExtensionService } from 'vscode/vscode/vs/workbench/services/extensions/common/extensions.service';
import 'vscode/vscode/vs/workbench/services/search/common/search';
import { SearchService } from '../common/searchService.js';
import { IUriIdentityService } from 'vscode/vscode/vs/platform/uriIdentity/common/uriIdentity.service';
import { SimpleWorkerClient, logOnceWebWorkerWarning } from 'vscode/vscode/vs/base/common/worker/simpleWorker';
import { Disposable, DisposableStore } from 'vscode/vscode/vs/base/common/lifecycle';
import { DefaultWorkerFactory } from 'vscode/vscode/vs/base/browser/defaultWorkerFactory';
import { memoize } from 'vscode/vscode/vs/base/common/decorators';
import { Schemas } from 'vscode/vscode/vs/base/common/network';
import { URI } from 'vscode/vscode/vs/base/common/uri';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { WebFileSystemAccess } from 'vscode/vscode/vs/platform/files/browser/webFileSystemAccess';
import { revive } from 'vscode/vscode/vs/base/common/marshalling';
import { TextSearchCompleteMessageType } from 'vscode/vscode/vs/workbench/services/search/common/searchExtTypes';

const _moduleId = "vs/workbench/services/search/browser/searchService";
let RemoteSearchService = class RemoteSearchService extends SearchService {
    constructor(modelService, editorService, telemetryService, logService, extensionService, fileService, instantiationService, uriIdentityService) {
        super(modelService, editorService, telemetryService, logService, extensionService, fileService, uriIdentityService);
        this.instantiationService = instantiationService;
        const searchProvider = this.instantiationService.createInstance(LocalFileSearchWorkerClient);
        this.registerSearchResultProvider(Schemas.file, 0 , searchProvider);
        this.registerSearchResultProvider(Schemas.file, 1 , searchProvider);
    }
};
RemoteSearchService = ( (__decorate([
    ( (__param(0, IModelService))),
    ( (__param(1, IEditorService))),
    ( (__param(2, ITelemetryService))),
    ( (__param(3, ILogService))),
    ( (__param(4, IExtensionService))),
    ( (__param(5, IFileService))),
    ( (__param(6, IInstantiationService))),
    ( (__param(7, IUriIdentityService)))
], RemoteSearchService)));
let LocalFileSearchWorkerClient = class LocalFileSearchWorkerClient extends Disposable {
    constructor(fileService, uriIdentityService) {
        super();
        this.fileService = fileService;
        this.uriIdentityService = uriIdentityService;
        this._onDidReceiveTextSearchMatch = ( (new Emitter()));
        this.onDidReceiveTextSearchMatch = this._onDidReceiveTextSearchMatch.event;
        this.queryId = 0;
        this._worker = null;
        this._workerFactory = ( (new DefaultWorkerFactory('localFileSearchWorker')));
    }
    sendTextSearchMatch(match, queryId) {
        this._onDidReceiveTextSearchMatch.fire({ match, queryId });
    }
    get fileSystemProvider() {
        return this.fileService.getProvider(Schemas.file);
    }
    async cancelQuery(queryId) {
        const proxy = await this._getOrCreateWorker().getProxyObject();
        proxy.cancelQuery(queryId);
    }
    async textSearch(query, onProgress, token) {
        try {
            const queryDisposables = ( (new DisposableStore()));
            const proxy = await this._getOrCreateWorker().getProxyObject();
            const results = [];
            let limitHit = false;
            await Promise.all(( (query.folderQueries.map(async (fq) => {
                const queryId = this.queryId++;
                queryDisposables.add(token?.onCancellationRequested(e => this.cancelQuery(queryId)) || Disposable.None);
                const handle = await this.fileSystemProvider.getHandle(fq.folder);
                if (!handle || !WebFileSystemAccess.isFileSystemDirectoryHandle(handle)) {
                    console.error('Could not get directory handle for ', fq);
                    return;
                }
                const reviveMatch = (result) => ({
                    resource: URI.revive(result.resource),
                    results: revive(result.results)
                });
                queryDisposables.add(this.onDidReceiveTextSearchMatch(e => {
                    if (e.queryId === queryId) {
                        onProgress?.(reviveMatch(e.match));
                    }
                }));
                const ignorePathCasing = this.uriIdentityService.extUri.ignorePathCasing(fq.folder);
                const folderResults = await proxy.searchDirectory(handle, query, fq, ignorePathCasing, queryId);
                for (const folderResult of folderResults.results) {
                    results.push(revive(folderResult));
                }
                if (folderResults.limitHit) {
                    limitHit = true;
                }
            }))));
            queryDisposables.dispose();
            const result = { messages: [], results, limitHit };
            return result;
        }
        catch (e) {
            console.error('Error performing web worker text search', e);
            return {
                results: [],
                messages: [{
                        text: ( localizeWithPath(_moduleId, 0, "Unable to search with Web Worker text searcher")), type: TextSearchCompleteMessageType.Warning
                    }],
            };
        }
    }
    async fileSearch(query, token) {
        try {
            const queryDisposables = ( (new DisposableStore()));
            let limitHit = false;
            const proxy = await this._getOrCreateWorker().getProxyObject();
            const results = [];
            await Promise.all(( (query.folderQueries.map(async (fq) => {
                const queryId = this.queryId++;
                queryDisposables.add(token?.onCancellationRequested(e => this.cancelQuery(queryId)) || Disposable.None);
                const handle = await this.fileSystemProvider.getHandle(fq.folder);
                if (!handle || !WebFileSystemAccess.isFileSystemDirectoryHandle(handle)) {
                    console.error('Could not get directory handle for ', fq);
                    return;
                }
                const caseSensitive = this.uriIdentityService.extUri.ignorePathCasing(fq.folder);
                const folderResults = await proxy.listDirectory(handle, query, fq, caseSensitive, queryId);
                for (const folderResult of folderResults.results) {
                    results.push({ resource: URI.joinPath(fq.folder, folderResult) });
                }
                if (folderResults.limitHit) {
                    limitHit = true;
                }
            }))));
            queryDisposables.dispose();
            const result = { messages: [], results, limitHit };
            return result;
        }
        catch (e) {
            console.error('Error performing web worker file search', e);
            return {
                results: [],
                messages: [{
                        text: ( localizeWithPath(_moduleId, 1, "Unable to search with Web Worker file searcher")), type: TextSearchCompleteMessageType.Warning
                    }],
            };
        }
    }
    async clearCache(cacheKey) {
        if (this.cache?.key === cacheKey) {
            this.cache = undefined;
        }
    }
    _getOrCreateWorker() {
        if (!this._worker) {
            try {
                this._worker = this._register(( (new SimpleWorkerClient(
                    this._workerFactory,
                    'vs/workbench/services/search/worker/localFileSearch',
                    this
                ))));
            }
            catch (err) {
                logOnceWebWorkerWarning(err);
                throw err;
            }
        }
        return this._worker;
    }
};
LocalFileSearchWorkerClient.__decorator = ( (__decorate([
    memoize
], LocalFileSearchWorkerClient.prototype, "fileSystemProvider", null)));
LocalFileSearchWorkerClient = ( (__decorate([
    ( (__param(0, IFileService))),
    ( (__param(1, IUriIdentityService)))
], LocalFileSearchWorkerClient)));

export { LocalFileSearchWorkerClient, RemoteSearchService };
